import type { ReactiveController, ReactiveControllerHost, TemplateResult } from 'lit'
import { css, html } from 'lit'
import { iconCaretRight, iconClose } from './icons'

export interface SidebarShellOpts {
  onOpen: () => void // 收合卡任一處點擊（含內部按鈕冒泡、空白處）→ 開啟 dialog
  onRequestClose: () => void // X 按鈕 → 呼 dialog.close()
  onClose: () => void // dialog 原生 close 事件（ESC/程式/X/backdrop 關閉後）→ 收斂狀態
  onBackdrop: (e: Event) => void // 點 ::backdrop 區時 → 呼 dialog.close()
}

// 收合內容由呼叫端（survey-element）依 phase 產生：未填 = 真實 Q1（可互動）、已填 = 精簡摘要卡、
// 載入中 = 占位卡。本檔只負責「收合插槽 + dialog 外殼」與 overlay 開關機制（沿用 G2、不變）。

// 收合卡的 Q1 內容 + 底部「查看所有選項」CTA（Figma 314:3461 Footer Actions）。
// CTA 無自身 handler：click 冒泡到 collapsed-slot 的 onOpen 開 dialog。
// q-row/q-num/q-body 沿用 activeStyles 的題目列版式（同一 shadow root）。
export function collapsedPromptView(q1: TemplateResult): TemplateResult {
  return html`
    <div class="q-row" @focusout=${resetQ1Scroll}>
      <span class="q-num">Q1</span>
      <div class="q-body">${q1}</div>
    </div>
    <div class="collapsed-cta">
      <button type="button" class="btn-primary collapsed-cta-btn">
        查看所有選項
        ${iconCaretRight()}
      </button>
    </div>
  `
}
// 鍵盤聚焦會捲動 .q-row（收合卡 Q1 唯一的捲動容器，見下方樣式）。它是 overflow: hidden，滾輪與觸控
// 捲不回來，所以焦點離開 Q1 時捲回頂端，題號與題目不會一直停在可見區外。
// 等焦點落定（下一個 task）再看 activeElement，而不是看 focusout 的 relatedTarget：視窗失焦時
// relatedTarget 也是 null，但焦點仍在這個選項上，那時捲回去會讓它回來時落在裁切區外。
function resetQ1Scroll(e: FocusEvent): void {
  const row = e.currentTarget as HTMLElement
  setTimeout(() => {
    const root = row.getRootNode() as Document | ShadowRoot
    if (!row.contains(root.activeElement))
      row.scrollTop = 0
  })
}

export const sidebarShellStyles = css`
  /* 收合插槽：整塊可點（空白處 → onOpen）；內部互動元素的 click 也冒泡到此 */
  .collapsed-slot { cursor: pointer; }
  /* Sidebar 收合卡（Figma 314:3461）：hero 300:180、內距 12、標題/Q-num 維持桌機字級
     （窄容器的 container query 會給 16px，但此稿指定 20px）。 */
  :host([layout='sidebar']) .collapsed-slot .frame {
    /* 收合卡固定為 Figma 300×470；無圖 fallback 時由 body 吃掉剩餘高度，
       讓問卷內容從上開始、CTA 固定在卡片底部。 */
    box-sizing: border-box;
    width: 300px;
    height: 470px;
    display: flex;
    flex-direction: column;
    --frame-pad-x: 12px;
    --frame-pad-top: 12px;
    --frame-pad-bottom: 12px;
  }
  :host([layout='sidebar']) .collapsed-slot .frame-header.has-hero:not([data-hero-broken]) {
    box-sizing: border-box;
    height: 180px;
    min-height: 180px;
    max-height: 180px;
    aspect-ratio: auto;
    padding: 32px 12px 12px;
  }
  /* 上面的 sidebar selector specificity 較高；Hero 圖載失敗時要明確解除其 3:2
     aspect-ratio，才能配合 frame.ts 的 compact fallback 收掉白色占位區。 */
  :host([layout='sidebar']) .collapsed-slot .frame-header.has-hero[data-hero-broken] {
    aspect-ratio: auto;
  }
  /* 收合卡 Compact（無封面或封面失敗）有 logo 時：高度隨內容、下限 160、上限 180，內容靠底
     （Figma 280:2020 的 Hero 166:1999 為 justify-end；手機 compact Hero 是同一元件，min-h 160、
     高度隨內容）。兩行標題時下面那條 padding-top 算式讓總高剛好 180；一行標題時內容只有 150，
     由 min-height 撐到 160，多出的 10px 落在 logo 上方。
     height 顯式寫 auto：省略的話會落回 geometry.ts scrollRules() 的
     height: min(var(--target-header-height), 面板−320)，sidebar 470 高會被夾到 148、兩行標題被裁掉。
     只給作答中的收合卡：致謝收合卡的 header 跟內容長（geometry.ts HEADER_FIXED）。
     無 logo 時不套用（見下面 data-logo-broken 那條）。 */
  :host([layout='sidebar']) .collapsed-slot .frame--answering > .frame-header:not(.has-hero):not([data-logo-broken]),
  :host([layout='sidebar']) .collapsed-slot .frame--answering > .frame-header.has-hero[data-hero-broken]:not([data-logo-broken]) {
    box-sizing: border-box;
    height: auto;
    min-height: 160px;
    max-height: 180px;
    justify-content: flex-end;
  }
  /* logo 載不到、或不顯示 logo（preview 尚未選 brand、或問卷 does_show_logo=false 時
     design-system/brands.ts resolveHeaderBrand 回 null；frame.ts 對這些情況都標 data-logo-broken）：內距 32/12/12（Figma 166:1999 pt32 px12 pb12），
     高度由 geometry.ts scrollRules() 決定（sidebar 470 高時為 148），不套用上面的內容高與下面
     「有 logo」的 padding-top 算式。 */
  :host([layout='sidebar']) .collapsed-slot .frame--answering > .frame-header:not(.has-hero)[data-logo-broken],
  :host([layout='sidebar']) .collapsed-slot .frame--answering > .frame-header.has-hero[data-hero-broken][data-logo-broken] {
    padding: 32px 12px 12px;
  }
  /* 有 logo：上內距讓兩行標題時總高剛好 180（Figma 280:2020 的 Hero 高）。算式：
       180 − pb12 − 標題兩行60（sidebar-shell 覆寫 .frame-title 為 font-size:20px，line-height
       沿用 frameHeadStyles 的 1.5 倍率 → 30px × 2 行）− header 的 flex gap 8（frameHeadStyles
       .frame-header { gap: 8px }，badge 與標題之間）− 窄容器 badge 高 28（frame.ts
       @container frame (max-width:639.98px) 的 .badge 規則；sidebar 容器寬 300 恆命中）−
       （logo 高 ＋ logo 與下一列距 12）＝ 180 − 12 − 60 − 8 − 28 − var(--mp-logo-row)
       ＝ 72 − var(--mp-logo-row)。--mp-logo-row 是既有變數（design-system/logos.ts
       logoVarsCss／geometry.ts GEOMETRY.LOGO.rowGap.popup），已經是「logo 高 + 12」的合併值、
       且 data-logo-broken 時自動歸零——不必再另外判斷 broken。
     一行標題（30）時內容只有 150，由上面的 min-height:160 撐開。 */
  :host([layout='sidebar']) .collapsed-slot .frame--answering > .frame-header:not(.has-hero):not([data-logo-broken]),
  :host([layout='sidebar']) .collapsed-slot .frame--answering > .frame-header.has-hero[data-hero-broken]:not([data-logo-broken]) {
    padding: calc(72px - var(--mp-logo-row)) 12px 12px;
  }
  /* Figma 280:2020 的 header 底線（border-b，色對應 --tvbs-survey-color-border-default #999，
     本 repo 同色 token 是 --mp-border-default，frameHeadStyles 的 .frame 外框已用它）。inset
     box-shadow 不佔版面，對應 Figma stroke 不參與 auto-layout，logo 位置與總高不受這條線影響。
     無 logo（data-logo-broken）的收合卡也有同一條線。 */
  :host([layout='sidebar']) .collapsed-slot .frame--answering > .frame-header:not(.has-hero),
  :host([layout='sidebar']) .collapsed-slot .frame--answering > .frame-header.has-hero[data-hero-broken] {
    box-shadow: inset 0 -1px 0 0 var(--mp-border-default);
  }
  :host([layout='sidebar']) .collapsed-slot .frame-title {
    font-size: 20px;
    letter-spacing: 1.2px;
  }
  :host([layout='sidebar']) .collapsed-slot .q-num {
    width: 32px;
    font-size: 20px;
    font-weight: 600;
  }
  /* 底部留一段淡出帶高的空白（見下面 .q-row）：鍵盤聚焦捲動時，最後一個選項也捲得到淡出帶之上。
     內容離 .q-row 底至少一個淡出帶高時，淡出帶蓋到的只有這段空白；不到的話，最後一列的下緣會被
     淡掉一些（此時 .q-row 也確實還能再捲）。 */
  :host([layout='sidebar']) .collapsed-slot .q-body {
    padding-bottom: var(--peek-fade);
  }
  :host([layout='sidebar']) .collapsed-slot .frame-body {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  /* Collapsed Q1 is a clipped preview, not an answering scroll surface. This context selector
     intentionally overrides frame.ts's popup/inline answering-body scroll contract. */
  :host([layout='sidebar']) .collapsed-slot .frame.frame--answering .frame-body {
    overflow: hidden;
  }
  /* Q1 只在這一層裁切（CTA 上緣），也是收合卡唯一的捲動容器：鍵盤聚焦到被裁掉的選項時，瀏覽器捲的
     是這一層，CTA 不動。下緣 --peek-fade 淡出（Figma 314:3461：最後一列在 CTA 上緣前淡掉），被截斷
     的那一列不會是一刀切齊的半截字；scroll-padding 再多留 focus ring 的 4px（host-styles.ts 的
     outline 2px + offset 2px），聚焦的選項連同外框都停在淡出帶之上。
     Chrome 120 以前只認 -webkit- 前綴，兩個都寫。 */
  :host([layout='sidebar']) .collapsed-slot .q-row {
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
    scroll-padding-bottom: calc(var(--peek-fade) + 4px);
    -webkit-mask-image: linear-gradient(#000 calc(100% - var(--peek-fade)), transparent);
    mask-image: linear-gradient(#000 calc(100% - var(--peek-fade)), transparent);
  }
  /* 收合卡 footer：白色漸層 + 滿寬「查看所有選項」主按鈕（Figma Footer Actions 166:2098）。
     按鈕無自身 handler —— click 冒泡到 .collapsed-slot 的 onOpen 開 dialog。 */
  .collapsed-cta {
    display: flex;
    margin: -20px calc(var(--frame-pad-x) * -1) calc(var(--frame-pad-bottom) * -1);
    padding: 12px;
    position: relative;
    /* Figma 166:2098 是 bg/overlay-soft → bg/overlay-weak，40% 就到實色。起點是底色的
       20% alpha 而非 transparent —— 帶子以上面的 -20px 疊在 Q1 上時，後者讓上緣完全不遮，
       疊到的選項整段透出來。sidebar 收合卡的 CTA 不疊（下一條 margin-top: auto 蓋掉 -20px），
       Q1 的截斷由 .q-row 的淡出處理。 */
    background: linear-gradient(var(--mp-overlay-soft), var(--mp-bg-base) 40%);
  }
  :host([layout='sidebar']) .collapsed-slot .collapsed-cta {
    flex: none;
    margin-top: auto;
  }
  /* 收合卡的 CTA 是滿寬的 Size=L Fill 鈕（Figma 166:2094：276 寬 × 48 高，卡片內距 12）。
     窄容器不降級成 Size=M —— 那條規則 scope 在 action bar 的 .nav 內。 */
  .collapsed-cta .btn-primary {
    width: 100%;
  }
  .collapsed-cta .btn-primary svg {
    width: 24px;
    height: 24px;
    flex: none;
  }
  /* 已填 / 占位精簡卡：標題 + 狀態行 + CTA */
  .summary-card {
    border: 1px solid var(--mp-border-default);
    border-radius: 8px;
    background: var(--mp-bg-base);
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 360px;
    margin: 0 auto;
  }
  .summary-title { font-size: 14px; font-weight: 600; }
  .summary-status { font-size: 13px; color: var(--mp-text-secondary); }
  .summary-cta {
    align-self: flex-start;
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    background: var(--mp-brand);
    color: var(--mp-text-inverse);
    font: inherit;
    font-size: 14px;
    cursor: pointer;
  }
  /* 預設（手機優先）：底部 bottom sheet、從下滑入、近滿版（Figma 314:3776：方角、無把手）。
     dialog top-layer 開啟時 position:fixed 以 viewport 為基準（不受宿主祖先影響）—— 這正是用 dialog 的理由。 */
  .mp-overlay {
    border: none;
    padding: 0;
    background: transparent;
    position: fixed;
    inset: auto 0 0 0;
    width: min(402px, 100vw);
    max-width: 100vw;
    margin: 0 auto;
    overflow: hidden;
  }
  .mp-overlay::backdrop { background: rgba(0, 0, 0, 0.6); }
  /* Panel 本身不捲動：answering 由 phase frame body 捲動，results/thank-you 由 outer phase frame
     捲動；X 留在 panel 外的 dialog 層固定。 */
  .mp-overlay-panel {
    position: relative;
    display: flex;
    flex-direction: column;
    background: var(--mp-bg-base);
    overflow: hidden;
    animation: mp-sheet-up 0.25s ease-out;
  }
  /* overlay 內讓 frame 填滿 sheet/modal —— 覆寫 inline sidebar 的 360px 窄欄 cap（那只為文章內用）。
     第二個 selector 是為了 specificity：單靠 .mp-overlay-panel .frame（0,2,0）會輸給
     :host([layout='sidebar']) .frame（0,3,0），sidebar 版型點開的 popup 內容就被鎖成
     360px 窄欄置中（Astro demo 回報的「popup 特別奇怪」即此因）。 */
  .mp-overlay-panel .frame,
  :host([layout='sidebar']) .mp-overlay-panel .frame {
    max-width: none;
    margin: 0;
    border: none;
    box-shadow: none;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  /* dialog 疊在頁面上：裡面捲到頂／底（或內容放得下）時不把滾輪交給背後的頁面。只掛在 dialog
     裡 —— 同一個 frame 也會出現在文章流中（inline、sidebar／modal 的收合卡），那裡要交還頁面。
     answering 由 .frame-body 捲、results／thank-you 由 .frame 捲，兩層都掛。 */
  .mp-overlay-panel .frame,
  .mp-overlay-panel .frame-body {
    overscroll-behavior: contain;
  }
  /* Compact（含封面失敗）且 logo 載不到時，標題從 32 起排會撞到右上角 X，要讓出空間
     （Figma 314:4091：pl-40 pr-80）。有 logo 時內容被 logo 列推到 X 下方，不需讓位
     （Figma 314:4171 px16、810:26191 px32）；正常 Cover 不套用。 */
  .mp-overlay-panel .frame-header:not(.has-hero)[data-logo-broken],
  .mp-overlay-panel .frame-header.has-hero[data-hero-broken][data-logo-broken] { padding-right: 72px; }
  @media (min-width: 640px) {
    .mp-overlay-panel .frame-header:not(.has-hero)[data-logo-broken],
    .mp-overlay-panel .frame-header.has-hero[data-hero-broken][data-logo-broken] { padding: 32px 80px 32px 40px; }
    /* 有 logo 的 compact：底距 32（Figma 810:26191 / 303:3156 / 810:26127）；上距 100 由 frame.ts 負責。 */
    .mp-overlay-panel .frame-header:not(.has-hero):not([data-logo-broken]),
    .mp-overlay-panel .frame-header.has-hero[data-hero-broken]:not([data-logo-broken]) { padding-bottom: 32px; }
  }
  /* 手機 popup 的結果／致謝 header 左右 12（Figma 315:5212 / 315:5232 / 322:5418 / 322:5632 /
     315:6435 / 315:6040）；作答頁維持 16（314:3910 / 314:4171）。只在窄 viewport：桌機由 frame.ts 決定。
     logo 失敗時右距仍由上方 X 讓位規則（specificity 較高）給 72。 */
  @media (max-width: 639.98px) {
    .mp-overlay-panel .frame--results > .frame-header,
    .mp-overlay-panel .frame--thank-you > .frame-header {
      padding-left: 12px;
      padding-right: 12px;
    }
  }
  @keyframes mp-sheet-up {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
  /* 桌機：貼底 800px bottom sheet、上圓角 56（Figma 166:2477）。不在 dialog/panel 設
     max-height，viewport clamp 與 scroll ownership 都由 internal phase frame 負責。 */
  @media (min-width: 640px) {
    .mp-overlay {
      inset: auto 0 0 0;
      width: min(800px, 100vw);
      margin: 0 auto;
    }
    .mp-overlay-panel {
      border-radius: 56px 56px 0 0;
    }
  }
  /* 關閉鈕：白底圓形 + 細框 + 陰影（Figma Close Button 492:20288）。掛在 dialog 層（panel 外）——
     panel 是唯一捲動容器，X 不隨內容捲走、固定在面板右上角。 */
  .mp-overlay-close {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    padding: 8px;
    border: 1px solid var(--mp-border-sub);
    border-radius: 9999px;
    background: var(--mp-bg-base);
    color: var(--mp-text-primary);
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.16);
    cursor: pointer;
  }
  .mp-overlay-close svg {
    width: 100%;
    height: 100%;
  }
  @media (min-width: 640px) {
    .mp-overlay-close {
      top: 20px;
      right: 20px;
    }
  }
`

interface ResultsSummaryOpts {
  title: string
  statusLine: string
  ctaLabel: string
}

// 已填（或載入中）的精簡收合卡：問卷標題 + 狀態行 + CTA。整張卡在 collapsed-slot 內、
// CTA 是無自身 handler 的 button —— 其 click（含鍵盤觸發）冒泡到 slot 的 onOpen 開啟 dialog。
export function resultsSummaryView(opts: ResultsSummaryOpts): TemplateResult {
  return html`
    <div class="summary-card">
      <span class="summary-title">${opts.title}</span>
      <span class="summary-status">${opts.statusLine}</span>
      <button type="button" class="summary-cta">${opts.ctaLabel}</button>
    </div>
  `
}

// 收合插槽 + dialog。收合內容（Q1 / 摘要卡 / 占位）由呼叫端產生並傳入；
// slot 的 @click 補捉非互動區開啟、內部互動元素（option/CTA 按鈕）的 click 也會冒泡到此。
export function sidebarShellView(
  opts: SidebarShellOpts,
  collapsedBody: TemplateResult,
  dialogFrame: TemplateResult,
  variant: 'sidebar' | 'modal' = 'sidebar',
): TemplateResult {
  // <dialog showModal> 原生提供 role=dialog / aria-modal / focus-trap / Esc 關閉。
  return html`
    <div class="collapsed-slot" @click=${opts.onOpen}>${collapsedBody}</div>
    <dialog class="mp-overlay ${variant === 'modal' ? 'mp-overlay--modal' : ''}" aria-label="問卷" @close=${opts.onClose} @click=${opts.onBackdrop}>
      <div class="mp-overlay-panel">${dialogFrame}</div>
      <button type="button" class="mp-overlay-close" aria-label="關閉" @click=${opts.onRequestClose}>${iconClose()}</button>
    </dialog>
  `
}

interface OverlayHost extends ReactiveControllerHost {
  renderRoot: DocumentFragment | HTMLElement
}

// 用 Lit ReactiveController 把「overlay 開關 + top-layer showModal + 背景捲動鎖 + shell 渲染」
// 這層從 survey-element 抽出、集中一處、與作答流程的 state 分離。
// Module-level body scroll-lock ref-count. Multiple overlays can coexist on one page
// (sidebar + modal). Each saving/restoring body overflow independently races — the last close
// restores the earlier 'hidden' and leaves the host page permanently locked. Ref-count
// converges it: the first lock records the original values, the last unlock restores them.
let scrollLockCount = 0
let scrollLockPrevOverflow = ''
let scrollLockPrevPaddingRight = ''

function lockBodyScroll(): void {
  if (scrollLockCount === 0) {
    const { body, documentElement } = document
    scrollLockPrevOverflow = body.style.overflow
    scrollLockPrevPaddingRight = body.style.paddingRight
    // 補回 scrollbar 寬度，避免鎖捲動時（classic scrollbar 環境）整頁變寬閃動。
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth
    body.style.overflow = 'hidden'
    if (scrollbarWidth > 0)
      body.style.paddingRight = `${scrollbarWidth}px`
  }
  scrollLockCount += 1
}

function unlockBodyScroll(): void {
  if (scrollLockCount === 0)
    return
  scrollLockCount -= 1
  if (scrollLockCount === 0) {
    document.body.style.overflow = scrollLockPrevOverflow
    document.body.style.paddingRight = scrollLockPrevPaddingRight
  }
}

export class SidebarOverlay implements ReactiveController {
  private isOpen = false
  private locked = false
  // 開啟前持有焦點的元素（觸發 overlay 的收合卡/CTA/選項）；關閉後還焦點回去，
  // 避免鍵盤/輔助科技使用者被丟回 <body>。
  private prevFocus: HTMLElement | null = null
  // onCloseNotify：dialog 任何方式關閉、狀態收斂後回呼一次（宿主據此對外發 pulse:close）。
  constructor(private host: OverlayHost, private onCloseNotify?: () => void) {
    host.addController(this)
  }

  private dialog(): HTMLDialogElement | null {
    return this.host.renderRoot.querySelector('.mp-overlay')
  }

  // 公開：收合卡任一處點擊、或收合 Q1 套用選擇後，都呼這個開啟 overlay（idempotent、可重複呼叫）。
  // 僅在 closed→open 轉換時記錄當前焦點 —— 重複呼叫不可覆蓋成 dialog 內部的元素。
  open = (): void => {
    if (!this.isOpen) {
      const root = this.host.renderRoot as Partial<ShadowRoot>
      this.prevFocus = (root.activeElement as HTMLElement | null) ?? null
    }
    this.isOpen = true
    this.host.requestUpdate()
  }

  // dialog 任何方式關閉（X / backdrop / ESC / 程式）都發 close 事件 —— 單一收斂點還原狀態與捲動
  private onClose = (): void => {
    this.isOpen = false
    if (this.locked) {
      unlockBodyScroll()
      this.locked = false
    }
    // 還焦點回觸發元素（節點仍在收合插槽內、Lit 重渲沿用）；已脫離 DOM 則 no-op。
    const prev = this.prevFocus
    this.prevFocus = null
    prev?.focus?.()
    this.host.requestUpdate()
    this.onCloseNotify?.()
  }

  // 公開程式化關閉：呼叫 dialog.close()，native close 事件走 onClose 收斂還原捲動/焦點。
  close = (): void => { this.dialog()?.close() }

  private requestClose = (): void => { this.close() }
  private onBackdrop = (e: Event): void => {
    const dlg = this.dialog()
    if (e.target === dlg)
      dlg?.close()
  }

  // showModal 必須在 render 後（dialog 已在 DOM）呼叫、且必須 imperative —— top layer 才逃得出宿主容器
  hostUpdated(): void {
    const dlg = this.dialog()
    // showModal 存在性防護：baseline 瀏覽器皆支援 <dialog>，這是給 jsdom（測試環境未實作）留的。
    if (this.isOpen && dlg && !dlg.open && typeof dlg.showModal === 'function') {
      dlg.showModal()
      if (!this.locked) {
        lockBodyScroll()
        this.locked = true
      }
    }
  }

  // 宿主節點被移除（第三方 SPA 導航 / admin 預覽卸載）時 dialog 不會發 close 事件、
  // onClose 不會跑 —— 此處兜底還原捲動、否則整個宿主頁會永久卡在 overflow:hidden。
  hostDisconnected(): void {
    if (this.locked) {
      unlockBodyScroll()
      this.locked = false
    }
    this.isOpen = false
  }

  render(
    collapsedBody: TemplateResult,
    dialogFrame: TemplateResult,
    variant: 'sidebar' | 'modal' = 'sidebar',
  ): TemplateResult {
    return sidebarShellView(
      {
        onOpen: this.open,
        onRequestClose: this.requestClose,
        onClose: this.onClose,
        onBackdrop: this.onBackdrop,
      },
      collapsedBody,
      dialogFrame,
      variant,
    )
  }
}
